import type { StateCreator } from 'zustand';
import { client } from '../../services/api';
import type {
  BespokeTemplateResponse,
  BespokeTemplateStatus,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { FormTreeQuestion } from '../../pages/formSchema/formSchemaTypes';
import type { VibecoderChatTurn } from '../../pages/formSchema/vibecoderChat';
import { sanitizeVibecoderAssistantDisplay } from '../../pages/formSchema/vibecoderChat';
import { validateLayoutJsonForSection } from '../../pages/formSchema/formSchemaUtils';
import { compactJsonTextForVibecoderApi } from '../../pages/formSchema/layoutCoverageFixPrompt';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import type { VibecoderSettingsSlice } from './vibecoderSettingsSlice';

export type EditorToast = {
  error: (title: string, description?: string) => void;
  success: (title: string, description?: string) => void;
};

/** The editable fields tracked for dirty detection. */
export type SectionEditorFields = {
  layoutJsonText: string;
  name: string;
  description: string;
  status: BespokeTemplateStatus;
};

/** Working state for one section's layout editor, keyed by sectionEditorDraftKey. */
export interface SectionEditorDraft extends SectionEditorFields {
  schemaVersionName: string;
  schemaVersionValue?: string | null;
  rogerDocFormType: RogerDocFormType;
  sectionName: string;
  /** null = creating a brand-new template. */
  editingTemplateId: string | null;
  chatMessages: VibecoderChatTurn[];
  chatPending: boolean;
  baseline: SectionEditorFields;
}

/** Identity + initial fields the parent hands to hydrate/seed a draft. */
export interface SectionEditorDraftSeed extends SectionEditorFields {
  schemaVersionName: string;
  schemaVersionValue?: string | null;
  rogerDocFormType: RogerDocFormType;
  sectionName: string;
  editingTemplateId: string | null;
}

export type SendSectionEditorChatArgs = {
  key: string;
  userTurn: VibecoderChatTurn;
  contextBundle: string;
  questions: FormTreeQuestion[];
  requireFullLayout?: boolean;
  toast: EditorToast;
};

export function isSectionEditorDraftDirty(
  draft: SectionEditorDraft | undefined
): boolean {
  if (!draft) return false;
  return (
    draft.layoutJsonText !== draft.baseline.layoutJsonText ||
    draft.name !== draft.baseline.name ||
    draft.description !== draft.baseline.description ||
    draft.status !== draft.baseline.status
  );
}

/**
 * True when a section draft is still the auto-created blank placeholder (e.g.
 * seeded before bespoke templates finished loading) and can be replaced with the
 * live/paused template without discarding user edits.
 */
export function shouldAdoptActiveTemplateDraft(
  draft: SectionEditorDraft | undefined,
  active: BespokeTemplateResponse | null | undefined
): boolean {
  if (!draft || !active?.layout_json || isSectionEditorDraftDirty(draft)) {
    return false;
  }
  const activeLayoutText = JSON.stringify(active.layout_json, null, 2);
  if (
    draft.editingTemplateId === active.id &&
    draft.layoutJsonText === activeLayoutText
  ) {
    return false;
  }
  return (
    draft.editingTemplateId == null && draft.layoutJsonText.trim() === '[]'
  );
}

function draftFromSeed(seed: SectionEditorDraftSeed): SectionEditorDraft {
  const fields: SectionEditorFields = {
    layoutJsonText: seed.layoutJsonText,
    name: seed.name,
    description: seed.description,
    status: seed.status,
  };
  return {
    schemaVersionName: seed.schemaVersionName,
    schemaVersionValue: seed.schemaVersionValue ?? null,
    rogerDocFormType: seed.rogerDocFormType,
    sectionName: seed.sectionName,
    editingTemplateId: seed.editingTemplateId,
    ...fields,
    chatMessages: [],
    chatPending: false,
    baseline: { ...fields },
  };
}

export interface FormSchemaEditorSlice {
  editorDraftByKey: Record<string, SectionEditorDraft>;
  /** Create the per-section draft on first visit only. */
  hydrateSectionEditorDraft: (args: {
    key: string;
    seed: SectionEditorDraftSeed;
  }) => void;
  /** Replace the draft when navigating to a different form (template or blank). */
  setSectionEditorDraft: (key: string, seed: SectionEditorDraftSeed) => void;
  setSectionEditorField: (
    key: string,
    patch: Partial<SectionEditorFields>
  ) => void;
  /**
   * Adopt name/description saved outside the editor (e.g. Versions menu) into
   * both working fields and baseline so a later layout Save cannot overwrite them.
   */
  syncSectionEditorTemplateDetails: (
    key: string,
    details: { name: string; description: string }
  ) => void;
  markSectionEditorSaved: (
    key: string,
    saved: BespokeTemplateResponse,
    /** Field values submitted with the save request; in-flight edits are kept. */
    submittedSnapshot?: SectionEditorFields
  ) => void;
  revertSectionEditorDraft: (key: string) => void;
  sendSectionEditorChat: (args: SendSectionEditorChatArgs) => Promise<void>;
}

function serverFieldsFromSaved(
  saved: BespokeTemplateResponse,
  draft: SectionEditorDraft
): SectionEditorFields {
  return {
    layoutJsonText: saved.layout_json
      ? JSON.stringify(saved.layout_json, null, 2)
      : draft.layoutJsonText,
    name: saved.name ?? draft.name,
    description: saved.description ?? draft.description,
    status: saved.status,
  };
}

/** Keep in-flight edits; adopt server values only for untouched fields. */
function mergeFieldsAfterSave(
  draft: SectionEditorDraft,
  serverFields: SectionEditorFields,
  submittedSnapshot: SectionEditorFields
): { fields: SectionEditorFields; baseline: SectionEditorFields } {
  const mergeOne = <K extends keyof SectionEditorFields>(key: K) => {
    if (draft[key] !== submittedSnapshot[key]) {
      return {
        value: draft[key],
        baseline: draft.baseline[key],
      };
    }
    return {
      value: serverFields[key],
      baseline: serverFields[key],
    };
  };

  const layout = mergeOne('layoutJsonText');
  const name = mergeOne('name');
  const description = mergeOne('description');
  const status = mergeOne('status');

  return {
    fields: {
      layoutJsonText: layout.value,
      name: name.value,
      description: description.value,
      status: status.value,
    },
    baseline: {
      layoutJsonText: layout.baseline,
      name: name.baseline,
      description: description.baseline,
      status: status.baseline,
    },
  };
}

function draftMatchesChatRequestSnapshot(
  draft: SectionEditorDraft,
  editingTemplateId: string | null,
  layoutJsonText: string
): boolean {
  return (
    draft.editingTemplateId === editingTemplateId &&
    draft.layoutJsonText === layoutJsonText
  );
}

const CHAT_RESPONSE_CONFLICT_NOTE =
  'Not applied — the layout or template changed while this request was in flight.';

export const formSchemaEditorSlice: StateCreator<
  FormSchemaEditorSlice & VibecoderSettingsSlice,
  [],
  [],
  FormSchemaEditorSlice
> = (set, get) => ({
  editorDraftByKey: {},

  hydrateSectionEditorDraft: ({ key, seed }) => {
    if (get().editorDraftByKey[key]) return;
    set(state => ({
      editorDraftByKey: {
        ...state.editorDraftByKey,
        [key]: draftFromSeed(seed),
      },
    }));
  },

  setSectionEditorDraft: (key, seed) => {
    set(state => ({
      editorDraftByKey: {
        ...state.editorDraftByKey,
        [key]: draftFromSeed(seed),
      },
    }));
  },

  setSectionEditorField: (key, patch) => {
    set(state => {
      const draft = state.editorDraftByKey[key];
      if (!draft) return {};
      return {
        editorDraftByKey: {
          ...state.editorDraftByKey,
          [key]: { ...draft, ...patch },
        },
      };
    });
  },

  syncSectionEditorTemplateDetails: (key, details) => {
    set(state => {
      const draft = state.editorDraftByKey[key];
      if (!draft) return {};
      return {
        editorDraftByKey: {
          ...state.editorDraftByKey,
          [key]: {
            ...draft,
            name: details.name,
            description: details.description,
            baseline: {
              ...draft.baseline,
              name: details.name,
              description: details.description,
            },
          },
        },
      };
    });
  },

  markSectionEditorSaved: (key, saved, submittedSnapshot) => {
    set(state => {
      const draft = state.editorDraftByKey[key];
      if (!draft) return {};
      const serverFields = serverFieldsFromSaved(saved, draft);
      const { fields, baseline } = submittedSnapshot
        ? mergeFieldsAfterSave(draft, serverFields, submittedSnapshot)
        : { fields: serverFields, baseline: serverFields };
      return {
        editorDraftByKey: {
          ...state.editorDraftByKey,
          [key]: {
            ...draft,
            editingTemplateId: saved.id,
            ...fields,
            baseline,
          },
        },
      };
    });
  },

  revertSectionEditorDraft: key => {
    set(state => {
      const draft = state.editorDraftByKey[key];
      if (!draft) return {};
      return {
        editorDraftByKey: {
          ...state.editorDraftByKey,
          [key]: { ...draft, ...draft.baseline },
        },
      };
    });
  },

  sendSectionEditorChat: async ({
    key,
    userTurn,
    contextBundle,
    questions,
    requireFullLayout = false,
    toast,
  }) => {
    const draft = get().editorDraftByKey[key];
    if (!draft || draft.chatPending) return;
    const trimmed = userTurn.content.trim();
    const images = userTurn.images ?? [];
    if (!trimmed && images.length === 0) return;

    const nextMessages: VibecoderChatTurn[] = [...draft.chatMessages, userTurn];
    const requestMessages = nextMessages.map(m => ({
      role: m.role,
      content: m.llmContent ?? m.content,
      ...(m.images && m.images.length > 0 ? { images: m.images } : {}),
    }));
    const currentLayoutText = compactJsonTextForVibecoderApi(
      draft.layoutJsonText
    );
    const requestEditingTemplateId = draft.editingTemplateId;
    const requestLayoutJsonText = draft.layoutJsonText;

    // Mark pending + append the user turn (keyed by section, so switching away
    // does not abort the request — its result lands back on this key).
    set(state => {
      const d = state.editorDraftByKey[key];
      if (!d) return {};
      return {
        editorDraftByKey: {
          ...state.editorDraftByKey,
          [key]: { ...d, chatMessages: nextMessages, chatPending: true },
        },
      };
    });

    const { vibecoderModelId, vibecoderTemperature } = get();
    try {
      const res = await client.post({
        url: '/vibecoder-layout-assistant/chat',
        body: {
          messages: requestMessages,
          context_bundle: contextBundle,
          current_layout_json_text: currentLayoutText,
          ...(requireFullLayout ? { require_full_layout: true } : {}),
          ...(vibecoderModelId ? { model: vibecoderModelId } : {}),
          ...(vibecoderTemperature != null &&
          !Number.isNaN(vibecoderTemperature)
            ? { temperature: vibecoderTemperature }
            : {}),
        },
        headers: { 'Content-Type': 'application/json' },
        responseType: 'json',
        throwOnError: true,
      });
      const data = res.data as {
        assistant_message?: string;
        layout_json?: unknown[] | null;
      };
      const displayAssistant = sanitizeVibecoderAssistantDisplay(
        data.assistant_message ?? ''
      );

      let layoutToApply: unknown[] | null = null;
      if (requireFullLayout) {
        if (!Array.isArray(data.layout_json)) {
          const hint = displayAssistant
            ? ` ${displayAssistant}`.replace(/\s+/g, ' ')
            : '';
          throw new Error(
            `The assistant did not return a layout JSON array.${hint || ' The model may have asked for clarification, or the response shape was unexpected.'}`
          );
        }
        const validation = validateLayoutJsonForSection(
          JSON.stringify(data.layout_json),
          questions
        );
        if (!validation.ok) {
          throw new Error(validation.error);
        }
        layoutToApply = validation.layout;
      } else {
        layoutToApply = Array.isArray(data.layout_json)
          ? data.layout_json
          : null;
      }

      const nextLayoutText =
        layoutToApply != null ? JSON.stringify(layoutToApply, null, 2) : null;

      let changed = false;
      let layoutConflict = false;
      set(state => {
        const d = state.editorDraftByKey[key];
        if (!d) return {};
        const stillCurrent = draftMatchesChatRequestSnapshot(
          d,
          requestEditingTemplateId,
          requestLayoutJsonText
        );
        const wouldApplyLayout = nextLayoutText != null;
        const staleResponse = !stillCurrent;
        layoutConflict = wouldApplyLayout && staleResponse;

        const assistantBody =
          displayAssistant ||
          (wouldApplyLayout && stillCurrent
            ? 'Updated Roger Layout Schema in the editor below.'
            : wouldApplyLayout
              ? 'The assistant returned a layout update.'
              : '');
        const assistantMsg: VibecoderChatTurn = {
          role: 'assistant',
          content: staleResponse
            ? assistantBody
              ? `${assistantBody}\n\n${CHAT_RESPONSE_CONFLICT_NOTE}`
              : CHAT_RESPONSE_CONFLICT_NOTE
            : assistantBody,
        };
        const updatedLayout =
          stillCurrent && wouldApplyLayout ? nextLayoutText : d.layoutJsonText;
        changed =
          stillCurrent &&
          wouldApplyLayout &&
          nextLayoutText !== d.layoutJsonText;
        return {
          editorDraftByKey: {
            ...state.editorDraftByKey,
            [key]: {
              ...d,
              chatMessages: [...d.chatMessages, assistantMsg],
              layoutJsonText: updatedLayout,
              chatPending: false,
            },
          },
        };
      });
      if (layoutConflict) {
        toast.error(
          'Layout not applied',
          'The editor changed while the assistant was working. See chat for the reply.'
        );
      } else if (changed) {
        toast.success(
          'Layout updated',
          'Applied to the Roger Layout Schema editor.'
        );
      }
    } catch (err) {
      set(state => {
        const d = state.editorDraftByKey[key];
        if (!d) return {};
        return {
          editorDraftByKey: {
            ...state.editorDraftByKey,
            [key]: { ...d, chatPending: false },
          },
        };
      });
      toast.error(
        'Assistant failed',
        getUserFacingApiMessage(err, 'Request failed')
      );
    }
  },
});
